import { Breadcrumb, Button, Label, TextInput } from "flowbite-react";
import { useEffect, useRef, useState, type FC } from "react";
import { HiHome, HiChevronDown, HiOfficeBuilding } from "react-icons/hi";
import NavbarSidebarLayout from "../layouts/navbar-sidebar";
import { AddCompanyModal, AllCompaniesTable, Pagination } from "../components/companiesList";
import { useNavigate } from "react-router";
import { useAppContext } from "../context/AppContext";
import Select from "react-select";
import { User } from "../services/user";
import { companyService } from "../services/main";

const CompaniesPage: FC = function () {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<{ value: any; label: any }[]>([]);
  const [selectedSector, setSelectedSector] = useState<string>("");
  const [selectedSize, setSelectedSize] = useState<string>("");
  const { companies, setCompanies } = useAppContext();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const dropdownRef = useRef(null) as any;

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const userService = new User("/users");
        const response = await userService.getAllUsers();
        setUsers(response.data.users || response.data || []);
      } catch (error) {
        console.error("Failed to fetch users", error);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await companyService.listCompanies();
        const sorted = response.data.companies.sort((a: any, b: any) => a.id - b.id);
        setCompanies(sorted);
      } catch (error) {
        console.error("Failed to fetch companies", error);
      }
    };
    fetchCompanies();
  }, [setCompanies]);

  const toggleFilterDropdown = () => {
    setIsFilterOpen((prev) => !prev);
  };

  const handleClickOutside = (event: any) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsFilterOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filteredCompanies = companies.filter((company: any) => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        (company.name?.toLowerCase().includes(searchLower)) ||
        (company.website?.toLowerCase().includes(searchLower)) ||
        (company.sector?.toLowerCase().includes(searchLower)) ||
        (company.city?.toLowerCase().includes(searchLower)) ||
        (company.country?.toLowerCase().includes(searchLower)) ||
        (company.phoneNumber?.toLowerCase().includes(searchLower));
      if (!matchesSearch) return false;
    }

    if (selectedUsers.length > 0) {
      const matchesUser = selectedUsers.some(u => company.accountManagerId === u.value);
      if (!matchesUser) return false;
    }

    if (selectedSector) {
      if (company.sector !== selectedSector) return false;
    }

    if (selectedSize) {
      if (company.size !== selectedSize) return false;
    }

    return true;
  });

  const totalItems = filteredCompanies.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedCompanies = filteredCompanies.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedUsers, selectedSector, selectedSize]);

  const uniqueSectors = Array.from(new Set(companies.map((c: any) => c.sector).filter(Boolean))).sort();
  const uniqueSizes = Array.from(new Set(companies.map((c: any) => c.size).filter(Boolean))).sort();

  return (
    <NavbarSidebarLayout>
      <div className="px-4 pt-6 pb-8">
        <div className="mb-6">
          <Breadcrumb className="mb-4">
            <Breadcrumb.Item onClick={() => navigate("/")}>
              <div className="flex items-center gap-x-3 cursor-pointer">
                <HiHome className="text-xl" />
                <span className="dark:text-white">Home</span>
              </div>
            </Breadcrumb.Item>
            <Breadcrumb.Item>Companies</Breadcrumb.Item>
          </Breadcrumb>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <HiOfficeBuilding className="text-green-600 dark:text-green-400" />
                All Companies
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Showing {filteredCompanies.length} of {companies.length} companies
                {searchTerm && ` matching "${searchTerm}"`}
              </p>
            </div>
          </div>
        </div>

        <div className="mb-6 flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="w-full sm:w-auto">
            <form>
              <Label htmlFor="users-search" className="sr-only">Search</Label>
              <div className="relative lg:w-64 xl:w-96">
                <TextInput
                  id="users-search"
                  name="users-search"
                  placeholder="Search companies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full"
                />
              </div>
            </form>
          </div>
          <div className="flex items-center space-x-2 sm:space-x-3">
            <AddCompanyModal />
            <div className="relative">
              <Button 
                color="gray" 
                type="button" 
                onClick={toggleFilterDropdown}
                className="hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                  className="h-4 w-4 mr-2 text-gray-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z"
                    clipRule="evenodd"
                  />
                </svg>
                Filter
                {(selectedUsers.length > 0 || selectedSector || selectedSize) && (
                  <span className="ml-2 bg-blue-600 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
                    {[
                      selectedUsers.length > 0 ? selectedUsers.length : 0,
                      selectedSector ? 1 : 0,
                      selectedSize ? 1 : 0
                    ].reduce((a, b) => a + b, 0)}
                  </span>
                )}
                <HiChevronDown className="ml-1.5 h-5 w-5" />
              </Button>
              {(selectedUsers.length > 0 || selectedSector || selectedSize) && (
                <button
                  className="absolute -top-1 -right-1 text-gray-500 hover:text-red-600 bg-white dark:bg-gray-800 rounded-full w-5 h-5 flex items-center justify-center text-xs border border-gray-200 dark:border-gray-700"
                  onClick={() => {
                    setSelectedUsers([]);
                    setSelectedSector("");
                    setSelectedSize("");
                  }}
                  title="Clear all filters"
                >
                  &#10005;
                </button>
              )}
              {isFilterOpen && (
                <div
                  ref={dropdownRef}
                  className="absolute z-20 mt-2 right-0 bg-white dark:bg-gray-800 border-0 rounded-xl shadow-xl p-6 min-w-[300px] max-w-[400px]"
                >
                  <div className="space-y-4">
                    <div>
                      <Label className="mb-2 block text-sm font-medium">Account Manager</Label>
                      <Select
                        options={users.map((u: any) => ({
                          value: u.id,
                          label: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || `User #${u.id}`
                        }))}
                        value={selectedUsers}
                        onChange={(vals) => setSelectedUsers(vals as any[])}
                        isMulti
                        isClearable={true}
                        placeholder="Select account managers..."
                        className="w-full text-sm"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        styles={{
                          menuPortal: base => ({ ...base, zIndex: 9999 })
                        }}
                      />
                    </div>
                    <div>
                      <Label className="mb-2 block text-sm font-medium">Sector</Label>
                      <select
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        value={selectedSector}
                        onChange={(e) => setSelectedSector(e.target.value)}
                      >
                        <option value="">All Sectors</option>
                        {uniqueSectors.map((sector: string) => (
                          <option key={sector} value={sector}>
                            {sector}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="mb-2 block text-sm font-medium">Company Size</Label>
                      <select
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        value={selectedSize}
                        onChange={(e) => setSelectedSize(e.target.value)}
                      >
                        <option value="">All Sizes</option>
                        {uniqueSizes.map((size: string) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                      <Button
                        color="gray"
                        size="xs"
                        onClick={() => {
                          setSelectedUsers([]);
                          setSelectedSector("");
                          setSelectedSize("");
                        }}
                        className="flex-1"
                      >
                        Clear All
                      </Button>
                      <Button
                        color="primary"
                        size="xs"
                        onClick={() => setIsFilterOpen(false)}
                        className="flex-1"
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
          <AllCompaniesTable paginatedCompanies={paginatedCompanies} />
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            setCurrentPage={setCurrentPage}
          />
        </div>
      </div>
    </NavbarSidebarLayout>
  );
};

export default CompaniesPage;
